import { X } from "lucide-react";

const AGENTS = ["structure", "orderflow", "crossmarket"];
const DIMS = [["regime", "Regime"], ["session", "Session"], ["direction", "Direction"]];
const accColor = (b) => (!b || b.n < 5 ? "var(--r2-text2)" : b.accuracy >= 0.55 ? "var(--r2-emerald)" : b.accuracy <= 0.45 ? "var(--r2-red)" : "var(--r2-text1)");
const pct = (v) => (v == null ? "—" : `${Math.round(v * 100)}%`);
const when = (iso) => (iso ? new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "");

const Delta = ({ agent, history }) => {
  const h = (history || []).find((x) => x.agent === agent);
  if (!h) return <span className="text-[var(--r2-text2)]">no change yet</span>;
  const up = h.new > h.old;
  return <span style={{ color: up ? "var(--r2-emerald)" : "var(--r2-red)" }} data-testid={`sb-delta-${agent}`}>{up ? "▲" : "▼"} {h.old} → {h.new}</span>;
};

const DimTable = ({ dim, label, sb }) => {
  const keys = [...new Set(AGENTS.flatMap((a) => Object.keys(sb[a]?.[dim] || {})))].sort();
  return (
    <div data-testid={`sb-dim-${dim}`}>
      <div className="mb-1 text-[7px] font-bold tracking-[0.2em] text-[var(--r2-text2)]">{label.toUpperCase()}</div>
      {keys.length === 0 && <div className="text-[var(--r2-text2)]">no evaluated candidates yet</div>}
      {keys.map((k) => (
        <div key={k} className="grid items-center gap-2 border-t border-[var(--r2-border)] py-[3px]" style={{ gridTemplateColumns: "110px repeat(3, minmax(0,1fr))" }}>
          <span className="truncate text-[var(--r2-text1)]">{k}</span>
          {AGENTS.map((a) => { const b = sb[a]?.[dim]?.[k]; return (
            <span key={a} className="r2-num" style={{ color: accColor(b) }} title={b ? `${b.hit}/${b.n} hits` : "no sample"} data-testid={`sb-cell-${dim}-${k}-${a}`}>
              {b ? pct(b.accuracy) : "—"}<span className="text-[var(--r2-text2)]"> n{b?.n ?? 0}</span>
            </span>); })}
        </div>
      ))}
    </div>
  );
};

export const MbtScoreboard = ({ s, onClose }) => {
  const sb = s.scoreboard || {}; const w = s.weights || {}; const hist = s.weight_history || [];
  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={onClose} data-testid="mbt2-scoreboard-modal">
      <div className="r2-panel w-[min(920px,94vw)] max-h-[86vh] overflow-auto p-4 text-[8.5px] font-mono" onClick={(e) => e.stopPropagation()}>
        <div className="mb-3 flex items-center justify-between">
          <div>
            <div className="text-[10px] font-bold tracking-[0.22em] text-[var(--r2-text1)]">AGENT SCOREBOARD · MBT V2</div>
            <div className="text-[var(--r2-text2)]">directional accuracy of each agent vs. what the market did 30 min later · weights re-fit once n ≥ 20 · {s.stats?.evaluated ?? 0} evaluated</div>
          </div>
          <button onClick={onClose} className="text-[var(--r2-text2)] hover:text-[var(--r2-text1)]" data-testid="mbt2-scoreboard-close"><X size={14} /></button>
        </div>
        <div className="mb-3 grid gap-2" style={{ gridTemplateColumns: "110px repeat(3, minmax(0,1fr))" }} data-testid="sb-header">
          <span className="text-[var(--r2-text2)]">agent</span>
          {AGENTS.map((a) => { const o = sb[a]?.overall; return (
            <div key={a} className="rounded-sm border border-[var(--r2-border)] px-2 py-1" data-testid={`sb-agent-${a}`}>
              <div className="font-bold uppercase tracking-[0.14em] text-[var(--r2-text1)]">{a}</div>
              <div><span style={{ color: accColor(o) }} className="r2-num text-[11px] font-bold">{pct(o?.accuracy)}</span><span className="text-[var(--r2-text2)]"> n{o?.n ?? 0}</span></div>
              <div>weight <b className="r2-num text-[var(--r2-cyan)]">{w[a]}</b> · <Delta agent={a} history={hist} /></div>
            </div>); })}
        </div>
        <div className="grid gap-4 md:grid-cols-3">{DIMS.map(([d, l]) => <DimTable key={d} dim={d} label={l} sb={sb} />)}</div>
        <div className="mt-4" data-testid="sb-weight-log">
          <div className="mb-1 text-[7px] font-bold tracking-[0.2em] text-[var(--r2-text2)]">WEIGHT CHANGES</div>
          {hist.length === 0 && <div className="text-[var(--r2-text2)]">no re-weighting yet — needs ≥ 20 evaluated candidates per agent</div>}
          {hist.map((h, i) => (
            <div key={i} className="flex flex-wrap gap-x-3 border-t border-[var(--r2-border)] py-[3px]">
              <span className="text-[var(--r2-text2)]">{when(h.at)}</span><span className="uppercase text-[var(--r2-text1)]">{h.agent}</span>
              <span style={{ color: h.new > h.old ? "var(--r2-emerald)" : "var(--r2-red)" }}>{h.old} → {h.new}</span>
              <span className="text-[var(--r2-text2)]">acc {pct(h.accuracy)} · {h.why}</span>
            </div>
          ))}
        </div>
        {Object.keys(sb._setup_stats || {}).length > 0 && (
          <div className="mt-4" data-testid="sb-setups">
            <div className="mb-1 text-[7px] font-bold tracking-[0.2em] text-[var(--r2-text2)]">SETUP FAMILIES (shadow)</div>
            {Object.entries(sb._setup_stats).map(([k, v]) => (
              <div key={k} className="flex gap-3 border-t border-[var(--r2-border)] py-[3px]"><span className="w-[160px] text-[var(--r2-text1)]">{k}</span><span>n{v.n}</span><span>win {pct(v.winrate)}</span>
                <span style={{ color: v.net_usd >= 0 ? "var(--r2-emerald)" : "var(--r2-red)" }}>{v.net_usd >= 0 ? "+" : "-"}${Math.abs(v.net_usd).toFixed(2)}</span></div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
